// =====================================================================
//  EDICIÓN DEL EXPEDIENTE Y DEL MARCO LEGAL — solo máster
//
//  Separado de Causa.tsx porque esa vista es de consulta para todo el
//  equipo y esto solo lo ve el máster.
// =====================================================================

import { useState } from 'react'
import { useGuardarArticulo, useGuardarCausa } from '../datos/consultas'
import { Alerta, Campo, Fila2, TituloCard, useAvisar } from '../componentes/ui'
import { fechaISO } from '../dominio/formato'
import type { Articulo, Causa, EstadoCausa } from '../tipos/dominio'

/**
 * Actualizar los datos del expediente.
 *
 * El campo que importa es `fecha_publicacion`. Hoy está vacío, y cargarlo
 * es la acción más importante de septiembre: la publicación en el Diario
 * Oficial fija los lotes definitivos y desde ella corre el plazo del
 * art. 147.
 *
 * El motor la valida. Si deja menos de 30 días corridos antes del remate,
 * la rechaza con MN147 — no hay forma de registrar una publicación que no
 * cumpla el plazo, ni siquiera desde SQL.
 */
export function EditarCausa({ causa }: { causa: Causa }) {
  const guardar = useGuardarCausa()
  const avisar = useAvisar()
  const [abierto, setAbierto] = useState(false)
  const [guardado, setGuardado] = useState(false)

  const [f, setF] = useState({
    fecha_publicacion: fechaISO(causa.fecha_publicacion),
    estado: causa.estado,
    tribunal: causa.tribunal,
    demandante: causa.demandante ?? '',
    abogado_patrocinante: causa.abogado_patrocinante ?? '',
    enlace_remate: causa.enlace_remate ?? '',
  })

  const aplicar = async () => {
    try {
      await guardar.mutateAsync({
        id: causa.id,
        cambio: {
          fecha_publicacion: f.fecha_publicacion || null,
          estado: f.estado,
          tribunal: f.tribunal.trim(),
          demandante: f.demandante.trim() || null,
          abogado_patrocinante: f.abogado_patrocinante.trim() || null,
          enlace_remate: f.enlace_remate.trim() || null,
        },
      })
      setGuardado(true)
      setTimeout(() => setGuardado(false), 3000)
    } catch (e) {
      avisar(e)
    }
  }

  if (!abierto) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <TituloCard>Actualizar el expediente</TituloCard>
        <p className="sub" style={{ marginBottom: 10 }}>
          {causa.fecha_publicacion
            ? 'La publicación ya está registrada. El resto de los datos cambia solo si lo modifica el tribunal.'
            : 'Falta registrar la fecha de publicación en el Diario Oficial. Es el dato que fija los lotes definitivos y desde el que corre el plazo del art. 147.'}
        </p>
        <button className="btn pri" onClick={() => setAbierto(true)}>
          Actualizar datos de la causa
        </button>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <TituloCard>
        Actualizar el expediente
        {guardado ? <span className="estado-campo ok">guardado</span> : null}
      </TituloCard>

      <Alerta tono="info" estilo={{ marginBottom: 14 }}>
        La fecha de publicación la valida el motor: si deja menos de 30 días corridos antes del
        remate, sin contar el día de publicación ni el del remate, la rechaza (art. 147). Para el
        remate del 21 de octubre, el último día posible es el 20 de septiembre.
      </Alerta>

      <Fila2>
        <Campo etiqueta="Publicación en el Diario Oficial">
          <input
            type="date"
            value={f.fecha_publicacion}
            onChange={(e) => setF({ ...f, fecha_publicacion: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Estado de la causa">
          <select value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value as EstadoCausa })}>
            <option value="TRAMITACION">En tramitación</option>
            <option value="REMATE_FIJADO">Remate fijado</option>
            <option value="REMATADA">Rematada</option>
            <option value="SUSPENDIDA">Suspendida</option>
            <option value="ARCHIVADA">Archivada</option>
          </select>
        </Campo>
      </Fila2>

      <Campo etiqueta="Tribunal">
        <input value={f.tribunal} onChange={(e) => setF({ ...f, tribunal: e.target.value })} />
      </Campo>

      <Fila2>
        <Campo etiqueta="Demandante">
          <input value={f.demandante} onChange={(e) => setF({ ...f, demandante: e.target.value })} />
        </Campo>
        <Campo etiqueta="Abogado patrocinante">
          <input
            value={f.abogado_patrocinante}
            onChange={(e) => setF({ ...f, abogado_patrocinante: e.target.value })}
          />
        </Campo>
      </Fila2>

      <Campo etiqueta="Enlace del remate">
        <input
          value={f.enlace_remate}
          onChange={(e) => setF({ ...f, enlace_remate: e.target.value })}
        />
      </Campo>

      <div style={{ display: 'flex', gap: 9 }}>
        <button className="btn pri" disabled={guardar.isPending} onClick={() => void aplicar()}>
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button className="btn" onClick={() => setAbierto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  )
}

/**
 * Una tarjeta de artículo, editable en el lugar.
 *
 * El texto está en la base y no en el código porque es el resumen de la
 * ley: cambia poco, pero cuando cambia lo hace por una reforma y no
 * debería exigir un despliegue.
 */
export function ArticuloCard({ articulo, editable }: { articulo: Articulo; editable: boolean }) {
  const guardar = useGuardarArticulo()
  const avisar = useAvisar()
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState(articulo.texto)

  const aplicar = async () => {
    try {
      await guardar.mutateAsync({ id: articulo.id, texto: texto.trim() })
      setEditando(false)
    } catch (e) {
      avisar(e)
    }
  }

  return (
    <div className="card" style={{ background: 'var(--panel2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <b style={{ color: 'var(--acc2)' }}>
          {articulo.enlace ? (
            <a href={articulo.enlace} target="_blank" rel="noreferrer">
              {articulo.codigo}
            </a>
          ) : (
            articulo.codigo
          )}
        </b>
        {editable && !editando ? (
          <button
            className="btn sm"
            style={{ padding: '2px 8px', fontSize: 11, flexShrink: 0 }}
            onClick={() => setEditando(true)}
          >
            editar
          </button>
        ) : null}
      </div>

      {editando ? (
        <>
          <textarea
            style={{
              width: '100%',
              marginTop: 8,
              minHeight: 130,
              background: 'var(--panel)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '9px 11px',
              fontSize: 12.5,
              resize: 'vertical',
            }}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 7, marginTop: 8 }}>
            <button className="btn sm pri" onClick={() => void aplicar()}>
              Guardar
            </button>
            <button
              className="btn sm"
              onClick={() => {
                setTexto(articulo.texto)
                setEditando(false)
              }}
            >
              Cancelar
            </button>
          </div>
        </>
      ) : (
        <p style={{ fontSize: 12.5, color: 'var(--txt2)', marginTop: 7 }}>{articulo.texto}</p>
      )}
    </div>
  )
}
